import { Component, computed, HostBinding, inject, input, linkedSignal } from '@angular/core';
import { FilterService } from '../services/filter.service';
import { QueryParamStateService } from '../services/query-param-state.service';
import { AsyncPipe } from '@angular/common';
import { ButtonComponent } from '@cumlaude/shared-components-buttons';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { FilterLabelComponent } from '../filter-label/filter-label.component';
import { HistoryService } from '@cumlaude/shared-services';
import { toSignal } from '@angular/core/rxjs-interop';
import { Selection } from '../shared/dashboard/base-dashboard/base-dashboard-config';
import { AD_HOC_SELECTION_ID, LeerlingSelectieService } from '../services/leerling-selectie.service';
import { switchMap } from 'rxjs/operators';
import { DatasnapUrlService } from '../services/datasnap-url.service';
import { FilterName } from '../services/filter-name';
import { of } from 'rxjs';
import { RLeerlingSelectie } from '@cumlaude/service-contract';
import { difference } from 'lodash-es';

@Component({
	selector: 'app-filter-bar',
	templateUrl: './filter-bar.component.html',
	styleUrls: ['./filter-bar.component.scss'],
	imports: [ButtonComponent, FilterLabelComponent, AsyncPipe, IconComponent],
})
export class FilterBarComponent {
	protected readonly filterService = inject(FilterService);
	protected readonly qp = inject(QueryParamStateService);
	protected readonly historyService = inject(HistoryService);
	protected readonly leerlingSelectieService = inject(LeerlingSelectieService);
	protected readonly datasnapUrlService = inject(DatasnapUrlService);

	readonly showTerug = input(false);
	readonly terugPageNiet = input<string>();

	readonly filters = toSignal(
		this.filterService.serviceRunning$.pipe(switchMap((running) => (running ? this.filterService.getFilledFiltersAndLeerlingselectie() : of()))),
		{
			initialValue: [],
		}
	);

	readonly filterIds = computed(() => this.filters().map(([name]) => this.filterId(name)));

	/**
	 * CL-8503 De initieel op een dashboard aanwezige filters moeten niet oplichten, alleen degene die er later bij komen.
	 */
	readonly filterIdsNew = linkedSignal<string[], Set<string>>({
		source: this.filterIds,
		computation: (newSource, previous) =>
			previous === undefined || previous.source.length === 0 ? new Set() : new Set(difference(newSource, previous.source)),
	});

	readonly dataRootSelection = input<Selection | undefined>(undefined);

	/**
	 * CL-8503 Als er een filter bij komt, moet hij even oplichten.
	 * Dit moet ook gebeuren als er een adhoc-leerlingselectie actief is en deze van inhoud verandert (zoals op
	 * Aanwezigheid > Overzicht kan gebeuren). Daarom krijgen adhoc-selecties een eigen filterId.
	 * Doordat er in de @for getracked wordt op dit filterId, wordt er bij verandering een nieuw component in de DOM gezet.
	 */
	filterId(filterName: FilterName) {
		if (filterName === 'x_leerlingselectie') {
			const selectie: RLeerlingSelectie = this.filters().find(([name]) => name === 'x_leerlingselectie')?.[1]?.[0];
			const adhocNaam = selectie?.id === AD_HOC_SELECTION_ID ? selectie.naam : '';
			return `x_leerlingselectie:${adhocNaam}`;
		}
		return filterName;
	}

	filterName(filterId: string): FilterName {
		return filterId.split(':')[0] as FilterName;
	}

	@HostBinding('class.disabled')
	get disabled() {
		return this.datasnapUrlService.isViewingSnapshot();
	}

	openFilterPanel() {
		this.filterService.filterPanelOpened$.next(true);
	}

	resetDashboard() {
		this.qp.resetDashboard().then(() => this.filterService.clearActiveNonDefault());
	}

	goBack() {
		const urlPart = this.terugPageNiet();
		if (urlPart) this.historyService.backToPageNot(urlPart);
	}
}
